import React, { useEffect, useRef } from "react";

const WA_NUMBER = "5491124289800";
const WA_MSG_PRESUP = encodeURIComponent(
  "Hola! Me comunico desde la web. Quisiera consultar precios y disponibilidad."
);

const included = [
  "3 horas de evento",
  "Para 50 personas (entre grandes y chicos)",
  "Animación para grandes y chicos",
  "Servicio de mesa",
  "Personal de cocina",
  "DJ",
  "Sonido e iluminación",
  "WiFi",
  "Emergencias médicas",
  "Invitación digital",
  "Laberinto pelotero 3 niveles",
  "Casita de niños",
  "PlayStation",
  "Sala blanda con Smart",
  "Pelotero bebés",
  "Cancha de fútbol techada",
  'Pantalla Smart 86"',
  "Escenario con mesitas candy",
  "Bolsitas de caramelos de fin de fiesta",
];

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el) => {
              el.classList.add("visible");
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="servicios"
      ref={sectionRef}
      className="py-24 bg-primary relative overflow-hidden"
      aria-labelledby="servicios-title"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="15" cy="15" r="2" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <p className="font-accent text-gold-light text-2xl mb-3">Lo que incluye tu festejo</p>
          <h2
            id="servicios-title"
            className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-4"
          >
            Nuestros servicios
          </h2>
          <p className="font-body text-white/70 text-lg max-w-2xl mx-auto">
            Elegí el paquete que mejor se adapta a tu festejo. Ambos se pueden personalizar para hacer tu evento único.
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Plan 1 */}
          <article
            className="reveal-left bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 hover:bg-white/15 transition-all duration-300"
            aria-labelledby="plan1-title"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gold-gradient flex items-center justify-center shadow-gold shrink-0">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div>
                <h3 id="plan1-title" className="font-display text-white text-xl font-bold">
                  Opción 1 — Premium
                </h3>
                <p className="font-body text-white/60 text-sm">Con menú infantil y bebidas</p>
              </div>
            </div>
            <p className="font-body text-white/80 mb-4">
              Incluye <strong className="text-gold-light">menú infantil completo</strong> y bebidas para los niños, ideal para que los chicos disfruten sin preocupaciones.
            </p>
            <div className="bg-white/10 rounded-2xl p-4">
              <p className="font-body text-white/60 text-sm italic">✓ Todo lo incluido en ambas opciones +</p>
              <p className="font-body text-white font-semibold mt-1">Menú infantil + bebidas para niños</p>
            </div>
          </article>

          {/* Plan 2 */}
          <article
            className="reveal-right bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 hover:bg-white/15 transition-all duration-300"
            aria-labelledby="plan2-title"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center shrink-0 border-2 border-accent/50">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <h3 id="plan2-title" className="font-display text-white text-xl font-bold">
                  Opción 2 — Estándar
                </h3>
                <p className="font-body text-white/60 text-sm">Sin menú infantil</p>
              </div>
            </div>
            <p className="font-body text-white/80 mb-4">
              Ideal si preferís traer tu propia comida para los niños o si el festejo es mayoritariamente de adultos.
            </p>
            <div className="bg-white/10 rounded-2xl p-4">
              <p className="font-body text-white/60 text-sm italic">✓ Todo lo incluido en ambas opciones</p>
              <p className="font-body text-white font-semibold mt-1">Sin menú infantil incluido</p>
            </div>
          </article>
        </div>

        {/* Included list */}
        <div className="reveal bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 md:p-10">
          <h3 className="font-display text-white text-2xl font-bold mb-2 text-center">
            Ambas opciones incluyen
          </h3>
          <p className="font-body text-white/60 text-center mb-8 text-sm">
            Todo lo que necesitás para un festejo completo e inolvidable
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" role="list">
            {included.map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-gold-light" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <span className="font-body text-white/85 text-sm">{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="font-body text-white/70 mb-4 text-sm">
              ¿Querés algo especial? El evento puede personalizarse según tus necesidades.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG_PRESUP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-body font-semibold text-white px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-gold"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E8C97A 60%, #C9A84C 100%)" }}
              aria-label="Consultar precio y personalizar evento"
            >
              Consultar precio y personalizar
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
